// Packages
import React from 'react';

// Services
import { apiFetchData } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import { IS_FRAME_SERVICE, setWindowParentLocation } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import LocalStorageManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/LocalStorageManipulationService';

// Parts
import Carousel from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/carousel';
import WishlistWidget from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/WishlistWidget';

// Icons
import {
    MdOutlinePercent,
    MdStar,
    MdWater,
    MdOutlineEditCalendar,
    MdArrowRight,
    MdOutlineShower,
    MdOutlineBedtime,
    MdPerson,
    MdOutlineDoorFront,
    MdOutlinePersonOutline,
} from 'react-icons/md';
import {
    dispatchTrackingEvent,
    resetEcommerceTrackingContext,
    buildBaseAnalyticsItem,
    SEARCH_RESULTS_CARD_EMPTY_SPACE_LINK,
    SEARCH_RESULTS_FACILITY_CLICK,
    SEARCH_RESULTS_FACILITY_IMAGE_LINK,
    SEARCH_RESULTS_FACILITY_TITLE_LINK,
    SEARCH_RESULTS_UNIT_CTA_LINK,
    SEARCH_RESULTS_UNIT_IMAGE_LINK,
    SEARCH_RESULTS_UNIT_TITLE_LINK,
    SELECT_ITEM,
    SEARCH_RESULTS_GALLERY_CHANGE_IMAGE,
    SEARCH_RESULTS_AVAILABILITY_MISSING_LINK,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';

export default class SearchResultsSingleUnitCard extends React.Component {
    constructor(props) {
        super(props);
        const { data, trackingData } = this.props;
        this.showPrice = !data.showGuestAdvice;
        this.indexOnPage = trackingData?.indexOnPage;
        this.unitCount = data?.units?.length ?? 1;
        // Logika prebačena iz lifecycle metode "componentDidMount" zbog toga da bi galerija bila dostupna i na SSR-u.
        let gallery = [];
        let unitIdToFirstGalleryImageIndexMapper = {};
        if (data && data.gallery && data.units) {
            gallery = [...data.gallery.items];
            unitIdToFirstGalleryImageIndexMapper = {};
            data.units.forEach((unit) => {
                unitIdToFirstGalleryImageIndexMapper[unit.unitId] = gallery.length;
                const unitImages = unit.gallery.items.map((image) => ({
                    ...image,
                    title: `${unit.type.toUpperCase()} ${unit.title}`,
                    unitId: unit.unitId,
                }));
                gallery.push(...unitImages);
            });
        }
        this.state = {
            singleUnit: data?.units[0], // Za single unit koristimo samo prvu jedinicu (bit ce potrebno doradit backend ako se odlučimo za ovu verziju)
            gallery,
            selectedUnitId: null,
            isGalleryLoading: false,
            isGalleryLoaded: false,
            unitIdToFirstGalleryImageIndexMapper,
            galleryIndexToJumpTo: null,
            anchorTagTarget: null,
            anchorTagRel: null,
            pageOnSearchResults: 1,
        };
    }

    componentDidMount() {
        const { smallDevice } = this.props;
        this.setState({ anchorTagTarget: !smallDevice ? '_blank' : null, anchorTagRel: !smallDevice ? 'noreferrer' : null });
    }

    componentDidUpdate(prevProps) {
        const { smallDevice, trackingData } = this.props;
        if (smallDevice !== prevProps.smallDevice) {
            this.setState({ anchorTagTarget: !smallDevice ? '_blank' : null, anchorTagRel: !smallDevice ? 'noreferrer' : null });
        }
        if (trackingData?.page !== prevProps.trackingData?.page) {
            this.setState({ pageOnSearchResults: trackingData?.page ?? null });
        }
    }

    onCurrentGalleryIndexChanged = async (index) => {
        const { gallery } = this.state;
        const selectedUnitId = gallery[index].unitId ?? null;
        this.setState({ selectedUnitId });
    };

    jumpToGallerySection = (moveDirection) => {
        const { gallery, unitIdToFirstGalleryImageIndexMapper, selectedUnitId } = this.state;
        let indexOffset = 0;
        if (moveDirection) {
            indexOffset = moveDirection === 'next' ? 1 : -1;
        }
        const galleryLength = gallery.length;
        const nextIndex = ((selectedUnitId ? unitIdToFirstGalleryImageIndexMapper[selectedUnitId] + indexOffset : indexOffset)
                            + galleryLength) % galleryLength;
        const newSelectedUnitId = gallery[nextIndex].unitId ?? null;
        this.setState({ galleryIndexToJumpTo: nextIndex, selectedUnitId: newSelectedUnitId }, () => {
            this.setState({ galleryIndexToJumpTo: null });
        });
    };

    getAdditionalData = (moveDirection) => {
        const { gallery, isGalleryLoaded, isGalleryLoading } = this.state;
        if (isGalleryLoaded || isGalleryLoading) return;
        const { data } = this.props;
        this.setState({ isGalleryLoading: true });
        apiFetchData('accommodation/private/search/facility-gallery/', `/${data.facilityId}`)
            .then((res) => {
                if (res.status !== 200) {
                    reportError(res);
                    return;
                }
                const newFacilityImages = res.data.facilityGallery?.flatMap((item) => item.images) ?? [];
                const newGallery = [gallery[0], ...newFacilityImages];
                const unitIdToFirstGalleryImageIndexMapper = {};
                const { unitsGallery } = res.data;
                unitsGallery.forEach((unitGallery) => {
                    if (!data.units.some((unit) => unit.unitId === unitGallery.unitId)) return;
                    unitIdToFirstGalleryImageIndexMapper[unitGallery.unitId] = newGallery.length;
                    const newUnitImages = unitGallery.images?.map(
                        (image) => ({ ...image, title: unitGallery.title, unitId: unitGallery.unitId }),
                    ) ?? [];
                    newGallery.push(
                        gallery.find((image) => image.unitId === unitGallery.unitId),
                        ...newUnitImages,
                    );
                });
                this.setState({
                    gallery: newGallery,
                    unitIdToFirstGalleryImageIndexMapper,
                    isGalleryLoaded: true,
                    isGalleryLoading: false,
                }, () => {
                    this.jumpToGallerySection(moveDirection);
                });
            })
            .catch((err) => reportError(err))
            .finally(() => this.setState({ isGalleryLoading: false }));
    };

    handleFacilityClickTrackingEvents = (redirectUrl, zeroUnitIndex = false) => {
        if (!IS_FRAME_SERVICE) {
            dispatchTrackingEvent(SEARCH_RESULTS_FACILITY_CLICK);
            resetEcommerceTrackingContext();
            const { data } = this.props;
            const { selectedUnitId, pageOnSearchResults } = this.state;
            const selectedUnit = data.units.find((unit) => unit.unitId === selectedUnitId) || data.units[0]; // fallback je prva jedinica (klik na sliku ili prazan prostor na kartici .)
            const selectedUnitIndex = data.units.findIndex((unit) => unit.unitId === selectedUnitId);
            const finalUnitIndex = selectedUnitIndex ? selectedUnitIndex + 1 : 1;
            const baseAnalyticsItem = buildBaseAnalyticsItem({ unitItem: selectedUnit });
            const item = {
                ...baseAnalyticsItem,
                currency: 'EUR',
                index: this.indexOnPage ? this.indexOnPage + 1 : 1,
                // TODO: Uklonit? Nisam pronašao slucaj u kojem data sadži ove podatke, tako da uvik završi na hardcode.
                item_category: data?.categoryName || 'Rezultati pretrage',
                item_list_id: data?.categoryId || 'search',
                item_list_name: data?.categorySlug || 'Kategorija Rezultati pretrage',
                unit_count: this.unitCount,
                unit_index: zeroUnitIndex ? 0 : finalUnitIndex,
                page: pageOnSearchResults ?? 1,
            };

            dispatchTrackingEvent(SELECT_ITEM, {
                ecommerce: {
                    item_list_id: data?.categoryId || 'search',
                    item_list_name: data?.categorySlug || 'Kategorija Rezultati pretrage',
                    items: [item],
                },
            });
            LocalStorageManipulationService.setTrackingItem(item);
        }
        if (redirectUrl) {
            if (IS_FRAME_SERVICE) {
                setWindowParentLocation(redirectUrl);
            } else {
                window.location = redirectUrl;
            }
        }
    };

    handleImageClickTrackingEvents = () => {
        this.handleFacilityClickTrackingEvents();
        if (!IS_FRAME_SERVICE) {
            dispatchTrackingEvent(SEARCH_RESULTS_FACILITY_IMAGE_LINK);
            dispatchTrackingEvent(SEARCH_RESULTS_UNIT_IMAGE_LINK);
        }
    };

    handleTitleClickTrackingEvents = () => {
        this.handleFacilityClickTrackingEvents();
        if (!IS_FRAME_SERVICE) {
            dispatchTrackingEvent(SEARCH_RESULTS_FACILITY_TITLE_LINK);
            dispatchTrackingEvent(SEARCH_RESULTS_UNIT_TITLE_LINK);
        }
    };

    handleEmptySpaceClickTrackingEvents = () => {
        this.handleFacilityClickTrackingEvents(null, true);
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(SEARCH_RESULTS_CARD_EMPTY_SPACE_LINK);
    };

    renderImageSourceElements = (item, index) => {
        const { bigimg } = this.props;
        if (!item.imageSourceURLLargeSizeAlternative) {
            return null;
        }

        return (
            <source
                srcSet={bigimg || index !== 0 ? `
                    ${item.image?.sourceURL} 365w,
                    ${item.imageSourceURLLargeSizeAlternative} 1275w
                ` : `
                    ${item.image?.sourceURL} 365w
                `}
                sizes="
                    (max-width: 991px) 92vw,
                    365px
                "
            />
        );
    };

    render() {
        const {
            data,
            firstCarouselImageLazy,
            isForMap,
            bigimg,
            smallDevice,
            wishlistedUnits,
            updateWishlistedUnits,
            openLoginModal,
            missingSearchCardAlertData,
            firstCarouselImageLcpPriority,
        } = this.props;
        const {
            singleUnit,
            gallery,
            isGalleryLoaded,
            isGalleryLoading,
            galleryIndexToJumpTo,
            anchorTagTarget,
            anchorTagRel,
        } = this.state;
        const imageWidth = isForMap ? 300 : 360;
        const imageHeight = isForMap ? 200 : 240;
        const mainURL = singleUnit.attractiveOffer?.reservationURL ?? singleUnit.resultUnitURL;
        const priceWithoutDiscount = singleUnit.attractiveOffer?.priceWithoutDiscount
            ?? singleUnit.fullFlexibleIntervalOffer?.priceWithoutDiscount
            ?? singleUnit.priceTextWithoutDiscount;
        const priceWithDiscount = singleUnit.attractiveOffer?.priceWithDiscount
            ?? singleUnit.fullFlexibleIntervalOffer?.priceWithDiscount
            ?? singleUnit.priceText;
        const shouldShowPrice = (this.showPrice && priceWithDiscount) || singleUnit.attractiveOffer;

        return data && (
            <div className="single-unit-card" data-id={singleUnit.unitId}>
                <a
                    className="single-unit-card__main-link"
                    href={mainURL}
                    aria-label={singleUnit.fullTitle}
                    target={anchorTagTarget}
                    rel={anchorTagRel}
                    onClick={this.handleEmptySpaceClickTrackingEvents}
                />
                {(data.rating || data.hasActiveDiscount) && (
                    <div className="single-unit-card-badges">
                        {data.rating && (
                            <div className="single-unit-card__rating">
                                <MdStar className="single-unit-card__rating-icon" />
                                <span>{data.rating}</span>
                            </div>
                        )}
                        {data.hasActiveDiscount && (
                            <div className="single-unit-card__discount">
                                <span className="single-unit-card__discount-text">
                                    {data.discountPercentText || <MdOutlinePercent className="single-unit-card__discount-icon" />}
                                </span>
                            </div>
                        )}
                    </div>
                )}
                <div role="presentation" className="single-unit-card__carousel-wrap">
                    {gallery.length > 0 && (
                        <Carousel
                            anchorTagUrl={mainURL}
                            anchorTagLabel={singleUnit.fullTitle}
                            onImageClick={this.handleImageClickTrackingEvents}
                            data={{ items: gallery, id: data.facilityId }}
                            isGalleryLoaded={isGalleryLoaded}
                            autoplay={false}
                            usedOnMap={isForMap}
                            imageWidth={imageWidth}
                            imageHeight={imageHeight}
                            imageSourceElements={this.renderImageSourceElements}
                            customObjectFit="cover"
                            firstImageLazy={firstCarouselImageLazy}
                            galleryFetching={isGalleryLoading}
                            onCurrentIndexChanged={this.onCurrentGalleryIndexChanged}
                            fetchGallery={this.getAdditionalData}
                            indexToJumpTo={galleryIndexToJumpTo}
                            maxHeight={bigimg ? null : imageHeight * 1.3}
                            isMobileScreen={smallDevice}
                            trackingEvents={{
                                searchResultsGalleryChangeImage: {
                                    eventName: SEARCH_RESULTS_GALLERY_CHANGE_IMAGE,
                                },
                            }}
                            searchResultsSingleUnitCardOverlayContent={
                                singleUnit.attractiveOffer?.intervalLengthString ? (
                                    <div className="single-unit-card__carousel-interval-length">
                                        <MdOutlineBedtime size={16} />
                                        <span>{singleUnit.attractiveOffer.intervalLengthString}</span>
                                    </div>
                                ) : null
                            }
                            imagePriorityLCP={firstCarouselImageLcpPriority}
                        />
                    )}
                    {!IS_FRAME_SERVICE && (
                        <WishlistWidget
                            unitsData={data.units}
                            wishlistedUnits={wishlistedUnits}
                            outsideUpdateWishlistedUnits={updateWishlistedUnits}
                            openLoginModal={openLoginModal}
                        />
                    )}
                </div>
                <div className="single-unit-card__content-wrap">
                    <div className="content__main">
                        <h2 className="content__title-wrap">
                            <a
                                className="content__title-link"
                                href={mainURL}
                                onClick={this.handleTitleClickTrackingEvents}
                                target={anchorTagTarget}
                                rel={anchorTagRel}
                            >
                                <span className="content__title-main">{data.genericTitle}</span>
                                <span className="content__title-info">{data.locationAndFacilityId}</span>
                            </a>
                        </h2>
                        {(data.distaceToBeach || data.freeCancel) && (
                            <div className="content__additional">
                                {data.distaceToBeach && (
                                    <p className="content__additional-item">
                                        <MdWater className="content__additional-item-icon" />
                                        <span>{data.distaceToBeach}</span>
                                    </p>
                                )}
                                {data.freeCancel && (
                                    <p className="content__additional-item">
                                        <MdOutlineEditCalendar className="content__additional-item-icon" />
                                        <span className="content__additional-item-txt">{data.freeCancel}</span>
                                    </p>
                                )}
                            </div>
                        )}
                        <div className="content__unit">
                            <div className="content__unit-header">
                                <h3 className="content__unit-title" title={`${singleUnit.type} ${singleUnit.title}`}>
                                    <span className="text-uppercase">{singleUnit.type}</span>
                                    <span> {singleUnit.title}</span>
                                </h3>
                                {singleUnit.deviationInterval && (
                                    <span className="content__unit-deviation">{singleUnit.deviationInterval}</span>
                                )}
                                {singleUnit.fullFlexibleIntervalOffer && (
                                    <span className="content__unit-deviation">{singleUnit.fullFlexibleIntervalOffer.interval}</span>
                                )}
                                {singleUnit.attractiveOffer && (
                                    <div>
                                        <span className="card-unit__attractive-offer-chip">
                                            <span className="card-unit__attractive-offer-chip-interval">
                                                {singleUnit.attractiveOffer.interval}
                                            </span>
                                            <span className="card-unit__attractive-offer-chip-percent">
                                                {singleUnit.attractiveOffer.discountPercent}
                                            </span>
                                        </span>
                                        {singleUnit.attractiveOffer?.callToActionString && (
                                            <div className="content__unit-offer-info-wrap-lm">
                                                <span className="content__unit-offer-info">{singleUnit.attractiveOffer.callToActionString}</span>
                                                <MdArrowRight className="content__unit-offer-info-icon content__unit-offer-info-icon--lm" />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="content__unit-details">
                                <span className="content__unit-details-item">
                                    <MdOutlineDoorFront className="content__unit-details-item-icon" />
                                    <span>
                                        <span>{data.roomsLabel}: </span>
                                        <span>{singleUnit.rooms}</span>
                                    </span>
                                </span>
                                <span className="content__unit-details-item">
                                    <MdOutlineShower className="content__unit-details-item-icon" />
                                    <span>
                                        <span>{data.bathroomsLabel}: </span>
                                        <span>{singleUnit.bathrooms}</span>
                                    </span>
                                </span>
                                <span className="content__unit-details-item">
                                    <MdOutlinePersonOutline className="content__unit-details-item-icon" />
                                    <span>
                                        <span>{data.guestsLabel}: </span>
                                        <span>{singleUnit.people}</span>
                                    </span>
                                </span>
                            </div>
                        </div>
                        {singleUnit.numberOfGuestsToDistributeAcrossOtherUnits && singleUnit.getDistributeAcrossOtherOptionsLabel && (
                            <div className="content__unit-offer-info-wrap">
                                <span className="content__unit-offer-info">
                                    +{singleUnit.numberOfGuestsToDistributeAcrossOtherUnits}
                                </span>
                                <span><MdPerson className="content__unit-offer-info-icon" /></span>
                                <span className="content__unit-offer-info">{singleUnit.getDistributeAcrossOtherOptionsLabel}</span>
                            </div>
                        )}
                        {/* Služi kako alocirani prostor da bi kartice bile ujednačene i kad nemaju određene podatke. */}
                        {!singleUnit.numberOfGuestsToDistributeAcrossOtherUnits
                        && !singleUnit.getDistributeAcrossOtherOptionsLabel
                        && !singleUnit.attractiveOffer?.callToActionString && (
                            <div className="content__unit-offer-info-wrap--mob-space">
                                <span className="content__unit-offer-info">&nbsp;</span>
                            </div>
                        )}
                    </div>
                    <div className="content__footer">
                        <div className="content__footer-info-wrap">
                            {shouldShowPrice ? (
                                <div className="content__footer-price-wrap">
                                    <span className="content__footer-price-title">{data.priceTotalLabel}: </span>
                                    {priceWithoutDiscount && (
                                        <span
                                            className="content__footer-price-without-discount"
                                            dangerouslySetInnerHTML={{ __html: priceWithoutDiscount }}
                                        />
                                    )}
                                    <span
                                        className="content__footer-price-main"
                                        dangerouslySetInnerHTML={{ __html: priceWithDiscount }}
                                    />
                                </div>
                            ) : (
                                missingSearchCardAlertData && (
                                    <button
                                        className="content__footer-missing-btn"
                                        type="button"
                                        onClick={() => {
                                            if (!IS_FRAME_SERVICE) {
                                                missingSearchCardAlertData?.onClick();
                                                dispatchTrackingEvent(SEARCH_RESULTS_AVAILABILITY_MISSING_LINK);
                                            }
                                        }}
                                        aria-label={missingSearchCardAlertData.text}
                                    >
                                        <span
                                            dangerouslySetInnerHTML={{ __html: missingSearchCardAlertData.text }}
                                        />
                                    </button>
                                )
                            )}
                        </div>
                        <a
                            href={mainURL}
                            aria-label={singleUnit.fullTitle}
                            target={anchorTagTarget}
                            rel={anchorTagRel}
                            className="content__footer-cta-link"
                            onClick={() => {
                                if (!IS_FRAME_SERVICE) {
                                    this.handleFacilityClickTrackingEvents();
                                    dispatchTrackingEvent(SEARCH_RESULTS_UNIT_CTA_LINK);
                                }
                            }}
                            data-js-target="_blank"
                        >
                            <span className="content__footer-cta-txt">{shouldShowPrice ? data.makeReservationText : data.checkAvailabilityText}</span>
                            <MdArrowRight className="content__footer-cta-icon" />
                        </a>
                    </div>
                </div>
            </div>
        );
    }
}
